import "./labelPrint.css";
import "./labelWorkspace.css";

import { useUser } from "@dashboard/auth";
import { useUserPermissions } from "@dashboard/auth/hooks/useUserPermissions";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { DashboardModal } from "@dashboard/components/Modal";
import {
  PermissionEnum,
  useLabelPrintRecordMutation,
  useLabelTemplateCreateMutation,
  useLabelTemplateDeleteMutation,
  useLabelTemplatesQuery,
  useLabelTemplateUpdateMutation,
  useProductLabelMarksQuery,
  useProductLabelTemplateAssignMutation,
  useProductLabelTemplatesQuery,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import {
  Box,
  Button,
  Checkbox,
  CheckedState,
  Dropdown,
  Input,
  List,
  Option,
  Select,
  Text,
} from "@saleor/macaw-ui-next";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Copy,
  Ellipsis,
  Pencil,
  Plus,
  Printer,
  SlidersHorizontal,
  Star,
  Wand2,
} from "lucide-react";
import {
  CSSProperties,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { IntlShape, useIntl } from "react-intl";

import { LabelCanvasEditor } from "./LabelCanvasEditor";
import { LabelPreviewCard } from "./LabelPreviewCard";
import {
  applyProductMarks,
  DEFAULT_LABEL_LAYOUT,
  LabelLayout,
  LabelResizeMode,
  parseLabelLayout,
  parseLabelMarks,
  resizeLayoutForLabelSize,
  withoutProductDataOverrides,
} from "./layout";
import { labelPrintDialogMessages as messages, labelTemplateMessages } from "./messages";
import { MillimeterInput } from "./MillimeterInput";
import {
  CUSTOM_PAPER_ID,
  DEFAULT_PAPER_PRESET_ID,
  getPaperPreset,
  PAPER_PRESETS,
} from "./paperSizes";
import {
  parsePrintSetup,
  PrintSetup,
  readUserPrintSetup,
  resolvePrintSetup,
  SHOP_50X30_ROLL_LAYOUT,
  writeUserPrintSetup,
} from "./printSetup";
import {
  computeSheetGrid,
  LABEL_GAP_MM,
  LabelData,
  PaperKind,
  PaperPreset,
  SHEET_MARGIN_MM,
} from "./types";
import { helpMessages } from "./views/LabelPrintingHelp/messages";

interface LabelPrintDialogProps {
  // Page heading in the sticky top bar.
  title: string;
  // Every label to print, in order — a variant appears once per copy.
  items: LabelData[];
  // What-to-print section (search + queue) shown above the template and
  // preview columns; hidden while the full-screen template editor is open.
  topContent?: ReactNode;
  // Shown as a Back button when the page was opened from somewhere else
  // (a product page) that the user may want to return to.
  onBack?: () => void;
  // Opens the printer set-up tips (header button, and a link beside the
  // paper size when a thermal roll is selected).
  onOpenHelp?: () => void;
}

const CUSTOM_TEMPLATE_SENTINEL = "__custom__";
const AUTO_TEMPLATE_SENTINEL = "auto";

// Every template tile is the same size whatever the template's proportions,
// so the gallery stays a compact grid: the label preview is fitted inside a
// fixed-height thumbnail box instead of setting the tile's height.
const TILE_THUMB_HEIGHT_PX = 76;

// How much shorter than the paper each printed page is drawn (see the print
// portal below): an exact fit spilled onto an extra, nearly blank page.
const PRINT_SAFETY_MM = 2;
const ROLL_PRINT_SAFETY_MM = 0.3;

// The template a person printed with last, restored when they come back:
// staff tend to print the same kind of label for weeks. Its paper setup comes
// from printSetup (theirs, else the template's). Per user in this browser.
interface LastPrintChoice {
  templateSelection: string;
}

const lastChoiceKey = (userId: string | undefined) =>
  `labelPrint.lastChoice.${userId ?? "anonymous"}`;

const readLastChoice = (key: string): LastPrintChoice | null => {
  try {
    const stored = JSON.parse(localStorage.getItem(key) ?? "null");

    return stored && typeof stored.templateSelection === "string"
      ? { templateSelection: stored.templateSelection }
      : null;
  } catch {
    return null;
  }
};

const writeLastChoice = (key: string, choice: LastPrintChoice) => {
  try {
    localStorage.setItem(key, JSON.stringify(choice));
  } catch {
    // Storage unavailable (private window): the choice just isn't remembered.
  }
};

const paperPresetLabel = (preset: PaperPreset, intl: IntlShape): string => {
  if (preset.kind === "sheet") {
    return intl.formatMessage(messages.paperSheet, {
      name: preset.sheetName,
      width: preset.labelWidthMm,
      height: preset.labelHeightMm,
    });
  }

  return intl.formatMessage(messages.paperRoll, {
    width: preset.labelWidthMm,
    height: preset.labelHeightMm,
  });
};

const sheetPresets = PAPER_PRESETS.filter(preset => preset.kind === "sheet");

// Quick "N per page" presets for custom sheet labels — columns × rows carved
// out of the page's printable area (inside the sheet margin, with the usual
// gap between labels), so "1" is one label filling the whole page, "2" is
// half the page, "4" is a quarter, etc.
const PAGE_FRACTION_PRESETS: Array<{ columns: number; rows: number }> = [
  { columns: 1, rows: 1 },
  { columns: 1, rows: 2 },
  { columns: 2, rows: 2 },
  { columns: 2, rows: 4 },
  { columns: 4, rows: 4 },
];

const fractionSizeMm = (
  pageWidthMm: number,
  pageHeightMm: number,
  columns: number,
  rows: number,
): { widthMm: number; heightMm: number } => ({
  widthMm: (pageWidthMm - 2 * SHEET_MARGIN_MM - (columns - 1) * LABEL_GAP_MM) / columns,
  heightMm: (pageHeightMm - 2 * SHEET_MARGIN_MM - (rows - 1) * LABEL_GAP_MM) / rows,
});

// Stand-in used wherever a sample item is needed (editor preview, gallery
// thumbnails) while `items` is still empty — never rendered as a real
// label, just keeps those previews from crashing on an undefined item.
const EMPTY_PREVIEW_ITEM: LabelData = {
  id: "",
  productId: "",
  name: "",
  sku: "",
  batchNumber: null,
  price: null,
  imageUrl: null,
  storefrontUrl: null,
};

const stopPropagation = (event: MouseEvent | KeyboardEvent) => event.stopPropagation();

interface TemplateForSetup {
  id: string;
  layout: unknown;
  defaultWidthMm?: number | null;
  defaultHeightMm?: number | null;
}

export const LabelPrintDialog = ({
  title,
  items,
  topContent,
  onBack,
  onOpenHelp,
}: LabelPrintDialogProps) => {
  const intl = useIntl();
  const notify = useNotifier();

  const [paperId, setPaperId] = useState(DEFAULT_PAPER_PRESET_ID);
  const [customKind, setCustomKind] = useState<PaperKind>("sheet");
  const [customPageId, setCustomPageId] = useState(sheetPresets[0].id);
  const [customWidthMm, setCustomWidthMm] = useState(50);
  const [customHeightMm, setCustomHeightMm] = useState(30);
  const [baseWidthMm, setBaseWidthMm] = useState<number | null>(null);
  // The label's own design size — set alongside customHeightMm whenever a
  // template is selected, but *not* touched by the "N per page" quick-size
  // presets below. A layout without a template size is designed (and its
  // text scaled, see LabelPreviewCard) at this size, so picking "1 per page"
  // enlarges the whole label instead of reflowing it.
  const [baseHeightMm, setBaseHeightMm] = useState<number | null>(null);
  // Whether the editor's own Label width/height inputs also update
  // baseWidthMm/baseHeightMm (scaling every field's font size along with the
  // canvas) or leave them alone (canvas resizes, content keeps its current
  // point size — the same "just reflow, don't rescale" behavior the N-per-
  // page quick sizes already use outside the editor).
  const [scaleContentOnResize, setScaleContentOnResize] = useState(true);
  const [pageFraction, setPageFraction] = useState<{ columns: number; rows: number } | null>(null);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [rotationDegrees, setRotationDegrees] = useState<0 | 90 | 180 | 270>(0);
  const [cropToFill, setCropToFill] = useState(false);
  // Roll layout of the current print setup (see printSetup.ts): set from the
  // person's or the template's setup whenever a template is picked.
  const [rollColumns, setRollColumns] = useState(SHOP_50X30_ROLL_LAYOUT.columns);
  const [rollRowsPerPage, setRollRowsPerPage] = useState(SHOP_50X30_ROLL_LAYOUT.rowsPerPage);
  const [rollLeftMarginMm, setRollLeftMarginMm] = useState(SHOP_50X30_ROLL_LAYOUT.leftMarginMm);
  const [rollRightMarginMm, setRollRightMarginMm] = useState(SHOP_50X30_ROLL_LAYOUT.rightMarginMm);
  const [rollMiddleGapMm, setRollMiddleGapMm] = useState(SHOP_50X30_ROLL_LAYOUT.middleGapMm);
  const [rollRowGapMm, setRollRowGapMm] = useState(SHOP_50X30_ROLL_LAYOUT.rowGapMm);

  // Rotation/zoom, roll spacing and the custom size inputs are rarely
  // touched, so they sit behind an "Adjust" toggle next to the paper select.
  const [isAdjustOpen, setAdjustOpen] = useState(false);
  // The preview shows one printed page (sheet, or a few roll rows) at a time,
  // so it always fits beside the templates without a scroll area of its own.
  const [previewPage, setPreviewPage] = useState(0);

  const [templateSelection, setTemplateSelection] = useState<string>(AUTO_TEMPLATE_SENTINEL);
  const [activeLayout, setActiveLayout] = useState<LabelLayout>(DEFAULT_LABEL_LAYOUT);
  const [layoutBeforeEditing, setLayoutBeforeEditing] = useState<LabelLayout>(DEFAULT_LABEL_LAYOUT);
  // Snapshot of every paper-size field, taken when entering the editor and
  // restored on "Discard changes". Picking a template to edit, duplicating
  // from the gallery, or typing a label size while inside the editor all
  // change these fields as a side effect (to match the label being worked
  // on) — without this, discarding only reverted the LAYOUT, leaving the
  // paper size stuck at whatever was last tried and making the outer
  // preview render the reverted layout's boxes at the wrong physical size
  // (badly wrapped/oversized text, as if the preview were "stuck").
  const [paperSnapshotBeforeEditing, setPaperSnapshotBeforeEditing] = useState<{
    paperId: string;
    customWidthMm: number;
    customHeightMm: number;
    baseWidthMm: number | null;
    baseHeightMm: number | null;
    pageFraction: { columns: number; rows: number } | null;
  } | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingBaseTemplateId, setEditingBaseTemplateId] = useState<string | null>(null);
  // Set when the editor was opened via a tile's "Duplicate" (a template the
  // user may not overwrite): the editor seeds from it but saves as a brand-new
  // personal template, named after it by default.
  const [copySourceName, setCopySourceName] = useState<string | null>(null);
  const [previousSelection, setPreviousSelection] = useState(AUTO_TEMPLATE_SENTINEL);
  const [isSaveFormOpen, setSaveFormOpen] = useState(false);
  const [saveNameInput, setSaveNameInput] = useState("");
  const [assignToProducts, setAssignToProducts] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(false);

  const uniqueItems = Array.from(new Map(items.map(item => [item.id, item])).values());
  const copiesById = items.reduce<Record<string, number>>((counts, item) => {
    counts[item.id] = (counts[item.id] ?? 0) + 1;

    return counts;
  }, {});
  const productIds = Array.from(new Set(items.map(item => item.productId))).filter(Boolean);
  const sampleItem = items[0] ?? EMPTY_PREVIEW_ITEM;

  const { data: templatesData, refetch: refetchTemplates } = useLabelTemplatesQuery();
  const { data: productTemplatesData, refetch: refetchProductTemplates } =
    useProductLabelTemplatesQuery({
      variables: { productIds },
      skip: productIds.length === 0,
    });
  const { data: productMarksData } = useProductLabelMarksQuery({
    variables: { ids: productIds },
    skip: productIds.length === 0,
  });
  const [createTemplate, createTemplateOpts] = useLabelTemplateCreateMutation();
  const [updateTemplate, updateTemplateOpts] = useLabelTemplateUpdateMutation();
  const [deleteTemplate, deleteTemplateOpts] = useLabelTemplateDeleteMutation();
  const [templateToDelete, setTemplateToDelete] = useState<{
    id: string;
    name: string;
    isDefault: boolean;
  } | null>(null);
  const [templateToRename, setTemplateToRename] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [renameInput, setRenameInput] = useState("");
  const [assignTemplate] = useProductLabelTemplateAssignMutation();
  const [recordPrint] = useLabelPrintRecordMutation();

  // Everyone who prints labels may create and edit templates: their own are
  // edited in place, shared ones (canEdit false) are edited as a personal
  // copy so the original stays as it was. Assigning a template to products
  // changes product data, so it stays with product managers.
  const userPermissions = useUserPermissions();
  const canManageTemplates = !!userPermissions?.some(
    permission =>
      permission.code === PermissionEnum.MANAGE_PRODUCTS ||
      permission.code === PermissionEnum.PRINT_LABELS,
  );
  const canAssignTemplates = !!userPermissions?.some(
    permission => permission.code === PermissionEnum.MANAGE_PRODUCTS,
  );

  const templates = useMemo(
    () => templatesData?.labelTemplates ?? [],
    [templatesData?.labelTemplates],
  );
  const defaultTemplate = templates.find(template => template.isDefault) ?? null;
  const { user } = useUser();
  const choiceKey = lastChoiceKey(user?.id);
  // Set once the saved choice has been applied (after the templates load),
  // so saving never overwrites it with the defaults first.
  const lastChoiceRestored = useRef(false);

  useEffect(() => {
    if (lastChoiceRestored.current || !templatesData) {
      return;
    }

    lastChoiceRestored.current = true;

    const saved = readLastChoice(choiceKey);

    if (saved && templates.some(candidate => candidate.id === saved.templateSelection)) {
      selectTemplate(saved.templateSelection);
    }
    // Runs once, when the templates first arrive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templatesData]);

  // Mirrors the API's uniqueness check (case-insensitive, trimmed) so the
  // problem shows next to the name field before anything is sent.
  const findTemplateNamed = (name: string, exceptId?: string | null) => {
    const wanted = name.trim().toLocaleLowerCase();

    return templates.find(
      template => template.id !== exceptId && template.name.trim().toLocaleLowerCase() === wanted,
    );
  };
  const productTemplateMap = new Map(
    (productTemplatesData?.productLabelTemplates ?? []).map(entry => [
      entry.productId,
      entry.template,
    ]),
  );

  const productMarksMap = new Map(
    mapEdgesToItems(productMarksData?.products)?.map(product => [
      product.id,
      parseLabelMarks(product.labelMarks ?? product.productType?.labelMarks),
    ]),
  );

  const isCustom = paperId === CUSTOM_PAPER_ID;
  const customPage = getPaperPreset(customPageId);
  const paper: PaperPreset = isCustom
    ? {
        id: CUSTOM_PAPER_ID,
        kind: customKind,
        labelWidthMm: customWidthMm,
        labelHeightMm: customHeightMm,
        pageWidthMm: customKind === "sheet" ? customPage.pageWidthMm : undefined,
        pageHeightMm: customKind === "sheet" ? customPage.pageHeightMm : undefined,
        sheetName: customKind === "sheet" ? customPage.sheetName : undefined,
      }
    : getPaperPreset(paperId);

  const paperOptions: Option[] = [
    ...PAPER_PRESETS.map(preset => ({
      value: preset.id,
      label: paperPresetLabel(preset, intl),
    })),
    { value: CUSTOM_PAPER_ID, label: intl.formatMessage(messages.paperCustom) },
  ];
  const sheetPageOptions: Option[] = sheetPresets.map(preset => ({
    value: preset.id,
    label: preset.sheetName ?? preset.id,
  }));

  const editingBaseTemplate = templates.find(t => t.id === editingBaseTemplateId);
  const customTemplateName = editingBaseTemplate
    ? intl.formatMessage(labelTemplateMessages.templateEditedUnsaved, {
        name: editingBaseTemplate.name,
      })
    : intl.formatMessage(labelTemplateMessages.templateCustomUnsaved);
  const selectedTemplateName =
    templateSelection === AUTO_TEMPLATE_SENTINEL
      ? intl.formatMessage(labelTemplateMessages.autoTemplateName)
      : templateSelection === CUSTOM_TEMPLATE_SENTINEL
        ? customTemplateName
        : (templates.find(t => t.id === templateSelection)?.name ?? "");

  const resolveItemTemplate = (item: LabelData) =>
    productTemplateMap.get(item.productId) ?? defaultTemplate;

  const resolveItemTemplateLayout = (item: LabelData): LabelLayout => {
    if (templateSelection === AUTO_TEMPLATE_SENTINEL) {
      const assigned = resolveItemTemplate(item);

      return assigned ? parseLabelLayout(assigned.layout) : DEFAULT_LABEL_LAYOUT;
    }

    return activeLayout;
  };
  // The template's layout minus the compliance marks this product doesn't
  // need (e.g. no CE/WEEE/RoHS on a wheel spacer).
  // A typed name/SKU/price/batch only makes sense for the one variant it
  // was typed for; with several variants queued each label shows its own.
  const resolveItemLayout = (item: LabelData): LabelLayout => {
    const layout = resolveItemTemplateLayout(item);

    return applyProductMarks(
      uniqueItems.length > 1 ? withoutProductDataOverrides(layout) : layout,
      productMarksMap.get(item.productId) ?? null,
    );
  };

  const resolveItemDesignSize = (item: LabelData) => {
    const template =
      templateSelection === AUTO_TEMPLATE_SENTINEL
        ? resolveItemTemplate(item)
        : templates.find(candidate => candidate.id === templateSelection);

    return {
      widthMm: template?.defaultWidthMm ?? baseWidthMm ?? paper.labelWidthMm,
      heightMm: template?.defaultHeightMm ?? baseHeightMm ?? paper.labelHeightMm,
    };
  };

  const rollOutputWidthMm =
    paper.kind === "roll"
      ? rollLeftMarginMm +
        rollColumns * paper.labelWidthMm +
        (rollColumns - 1) * rollMiddleGapMm +
        rollRightMarginMm
      : paper.labelWidthMm;
  // One roll "page" per row of labels: the label sits at the top and the gap
  // to the next row follows it, so the page height is the label pitch.
  const rollRowHeightMm = paper.labelHeightMm + rollRowGapMm;
  // Many label printers feed a whole page of several rows per job (e.g. 2 × 2
  // labels); the page sent must be that size, or the printer centres and
  // scales a one-row page inside its own.
  const rollPageHeightMm = rollRowsPerPage * rollRowHeightMm;
  const rollLabelsPerPage = rollColumns * rollRowsPerPage;

  const resolveItemPrintSize = (item: LabelData) => {
    if (!pageFraction || paper.kind !== "sheet" || !paper.pageWidthMm || !paper.pageHeightMm) {
      return { widthMm: paper.labelWidthMm, heightMm: paper.labelHeightMm };
    }

    const cell = fractionSizeMm(
      paper.pageWidthMm,
      paper.pageHeightMm,
      pageFraction.columns,
      pageFraction.rows,
    );
    const template =
      templateSelection === AUTO_TEMPLATE_SENTINEL ? resolveItemTemplate(item) : null;
    const designWidth = template?.defaultWidthMm ?? baseWidthMm ?? paper.labelWidthMm;
    const designHeight = template?.defaultHeightMm ?? baseHeightMm ?? paper.labelHeightMm;
    const scale = Math.min(cell.widthMm / designWidth, cell.heightMm / designHeight);

    return { widthMm: designWidth * scale, heightMm: designHeight * scale };
  };

  const currentPrintSetup = (): PrintSetup => ({
    paperId,
    customKind,
    customPageId,
    customWidthMm,
    customHeightMm,
    pageFraction,
    roll: {
      columns: rollColumns,
      rowsPerPage: rollRowsPerPage,
      leftMarginMm: rollLeftMarginMm,
      rightMarginMm: rollRightMarginMm,
      middleGapMm: rollMiddleGapMm,
      rowGapMm: rollRowGapMm,
    },
    rotationDegrees,
    zoomPercent,
    cropToFill,
  });

  const applyPrintSetup = (setup: PrintSetup) => {
    setPaperId(setup.paperId);
    setCustomKind(setup.customKind);
    setCustomPageId(setup.customPageId);
    setCustomWidthMm(setup.customWidthMm);
    setCustomHeightMm(setup.customHeightMm);
    setPageFraction(setup.pageFraction);
    setRollColumns(setup.roll.columns);
    setRollRowsPerPage(setup.roll.rowsPerPage);
    setRollLeftMarginMm(setup.roll.leftMarginMm);
    setRollRightMarginMm(setup.roll.rightMarginMm);
    setRollMiddleGapMm(setup.roll.middleGapMm);
    setRollRowGapMm(setup.roll.rowGapMm);
    setRotationDegrees(setup.rotationDegrees);
    setZoomPercent(setup.zoomPercent);
    setCropToFill(setup.cropToFill);
  };

  // The template's own setup (stored in its layout by "Use as this
  // template's default"), or the default for its size.
  const templatePrintSetup = (template: TemplateForSetup | null | undefined) =>
    resolvePrintSetup(
      null,
      template ? parsePrintSetup(parseLabelLayout(template.layout).printSetup) : null,
      template?.defaultWidthMm,
      template?.defaultHeightMm,
    );

  // What a template prints with for this person: their own setup if they
  // changed it, else the template's.
  const printSetupFor = (templateKey: string, template: TemplateForSetup | null | undefined) =>
    readUserPrintSetup(user?.id, templateKey) ?? templatePrintSetup(template);

  // Puts a template's paper setup in place, with the template's design size.
  const matchPaperToTemplate = (template: TemplateForSetup, templateKey = template.id) => {
    applyPrintSetup(printSetupFor(templateKey, template));

    if (!template.defaultWidthMm || !template.defaultHeightMm) {
      return false;
    }

    setBaseWidthMm(template.defaultWidthMm);
    setBaseHeightMm(template.defaultHeightMm);

    return true;
  };

  // "Auto" mode resolves each product's own assigned (or site-default)
  // template, which — like explicitly picking one — may be designed for a
  // specific physical size. Seed the paper size from whatever the first
  // queued item resolves to, once, the same way explicit selection does;
  // only while the paper is still untouched, so it never fights a size the
  // user picked on purpose.
  const autoSetupApplied = useRef(false);

  useEffect(() => {
    if (
      autoSetupApplied.current ||
      templateSelection !== AUTO_TEMPLATE_SENTINEL ||
      items.length === 0 ||
      !templatesData
    ) {
      return;
    }

    const template = resolveItemTemplate(items[0]);

    autoSetupApplied.current = true;

    if (template) {
      matchPaperToTemplate(template, AUTO_TEMPLATE_SENTINEL);
    }
    // Only when the queue first gets items (or templates load) — not on every
    // render: the paper fields flip as a *result* of this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, defaultTemplate, productTemplateMap, templatesData]);

  const selectedSavedTemplate = templates.find(t => t.id === templateSelection) ?? null;
  // The template whose setup the paper fields currently belong to.
  const setupTemplate =
    templateSelection === AUTO_TEMPLATE_SENTINEL
      ? items[0]
        ? resolveItemTemplate(items[0])
        : null
      : selectedSavedTemplate;
  const setupKey = JSON.stringify(currentPrintSetup());

  useEffect(() => {
    // An unsaved layout from the editor isn't a template to come back to.
    if (!lastChoiceRestored.current || templateSelection === CUSTOM_TEMPLATE_SENTINEL) {
      return;
    }

    // Debounced: picking a template updates many fields, and only the
    // settled result is the person's setup.
    const timer = window.setTimeout(() => {
      writeLastChoice(choiceKey, { templateSelection });

      const setup = currentPrintSetup();
      const sameAsTemplate =
        JSON.stringify(setup) === JSON.stringify(templatePrintSetup(setupTemplate));

      // Only a setup that differs from the template's is the person's own, so
      // a later change to the template's default still reaches them.
      writeUserPrintSetup(user?.id, templateSelection, sameAsTemplate ? null : setup);
    }, 400);

    return () => window.clearTimeout(timer);
    // currentPrintSetup is captured through setupKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choiceKey, templateSelection, setupKey, setupTemplate?.id, setupTemplate?.layout]);

  const computedSheetGrid =
    paper.kind === "sheet" && paper.pageWidthMm && paper.pageHeightMm
      ? computeSheetGrid(
          paper.pageWidthMm,
          paper.pageHeightMm,
          paper.labelWidthMm,
          paper.labelHeightMm,
        )
      : null;
  const sheetGrid = pageFraction
    ? {
        columns: pageFraction.columns,
        rows: pageFraction.rows,
        perPage: pageFraction.columns * pageFraction.rows,
      }
    : computedSheetGrid;

  const handlePrint = async () => {
    const printRoot = document.querySelector(".labelPrintPortalRoot");

    if (printRoot) {
      const pendingQrCodes = Array.from(
        printRoot.querySelectorAll<SVGSVGElement>('svg[data-label-asset="pending"]'),
      );

      await Promise.all(
        pendingQrCodes.map(
          svg =>
            new Promise<void>(resolve => {
              if (svg.dataset.labelAsset !== "pending") {
                resolve();

                return;
              }

              const observer = new MutationObserver(() => {
                if (svg.dataset.labelAsset === "ready") {
                  observer.disconnect();
                  resolve();
                }
              });

              observer.observe(svg, { attributes: true, attributeFilter: ["data-label-asset"] });
            }),
        ),
      );

      const images = Array.from(printRoot.querySelectorAll("img"));

      await Promise.all(
        images.map(
          image =>
            new Promise<void>(resolve => {
              if (image.complete) {
                resolve();

                return;
              }

              image.addEventListener("load", () => resolve(), { once: true });
              image.addEventListener("error", () => resolve(), { once: true });

              if (image.complete) {
                resolve();
              }
            }),
        ),
      );

      await new Promise<void>(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
    }

    // Printing happens in the browser, so tell the server for the activity log
    // (fire and forget: a failed audit call must never block printing).
    recordPrint({
      variables: {
        items: uniqueItems.map(item => ({
          id: item.id,
          name: item.sku ? `${item.name} (${item.sku})` : item.name,
          copies: copiesById[item.id] ?? 1,
        })),
        templateName: selectedTemplateName,
      },
    }).catch(() => undefined);

    window.print();
  };

  // Sheet preview: the page drawn to scale (margins and gaps as fractions of
  // the page), sized so a whole page fits the screen height beside the
  // template list. Roll preview: a few rows at the roll's real proportions.
  const sheetPageWidthMm = paper.pageWidthMm ?? 210;
  const sheetPageHeightMm = paper.pageHeightMm ?? 297;
  const sheetPrintableWidthMm = sheetPageWidthMm - 2 * SHEET_MARGIN_MM;
  const sheetPrintableHeightMm = sheetPageHeightMm - 2 * SHEET_MARGIN_MM;
  const sheetColumns = sheetGrid?.columns ?? 1;
  const sheetRows = sheetGrid?.rows ?? 1;
  const sheetCellWidthMm =
    (sheetPrintableWidthMm - (sheetColumns - 1) * LABEL_GAP_MM) / sheetColumns;
  const previewPageStyle: CSSProperties =
    paper.kind === "sheet"
      ? {
          display: "grid",
          boxSizing: "border-box",
          width: `min(100%, calc((100vh - 280px) * ${sheetPageWidthMm / sheetPageHeightMm}))`,
          aspectRatio: `${sheetPageWidthMm} / ${sheetPageHeightMm}`,
          margin: "0 auto",
          padding: `${(SHEET_MARGIN_MM / sheetPageWidthMm) * 100}%`,
          background: "#fff",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.18)",
          gridTemplateColumns: `repeat(${sheetColumns}, 1fr)`,
          gridTemplateRows: `repeat(${sheetRows}, 1fr)`,
          columnGap: `${(LABEL_GAP_MM / sheetPrintableWidthMm) * 100}%`,
          rowGap: `${(LABEL_GAP_MM / sheetPrintableHeightMm) * 100}%`,
          justifyItems: "center",
          alignItems: "center",
        }
      : {
          display: "grid",
          boxSizing: "border-box",
          width: `min(100%, calc((100vh - 280px) * ${rollOutputWidthMm / rollPageHeightMm}))`,
          aspectRatio: `${rollOutputWidthMm} / ${rollPageHeightMm}`,
          margin: "0 auto",
          background: "#fff",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.18)",
          gridTemplateColumns: `repeat(${rollColumns}, 1fr)`,
          gridTemplateRows: `repeat(${rollRowsPerPage}, ${(paper.labelHeightMm / rollPageHeightMm) * 100}%)`,
          columnGap: `${(rollMiddleGapMm / rollOutputWidthMm) * 100}%`,
          rowGap: `${(rollRowGapMm / rollPageHeightMm) * 100}%`,
          paddingLeft: `${(rollLeftMarginMm / rollOutputWidthMm) * 100}%`,
          paddingRight: `${(rollRightMarginMm / rollOutputWidthMm) * 100}%`,
          alignContent: "start",
          alignItems: "start",
        };
  // Width of one label inside its preview grid cell.
  const previewLabelWidth = (widthMm: number) =>
    paper.kind === "sheet" ? `min(100%, ${(widthMm / sheetCellWidthMm) * 100}%)` : "100%";

  const pageSizeRule =
    paper.kind === "sheet"
      ? `@page { size: ${paper.pageWidthMm}mm ${paper.pageHeightMm}mm; margin: ${SHEET_MARGIN_MM}mm; }`
      : `@page { size: ${rollOutputWidthMm}mm ${rollPageHeightMm}mm; margin: 0; }`;

  const selectTemplate = (templateId: string) => {
    setTemplateSelection(templateId);

    if (templateId === AUTO_TEMPLATE_SENTINEL) {
      const template = items[0] ? resolveItemTemplate(items[0]) : null;

      if (template) {
        matchPaperToTemplate(template, AUTO_TEMPLATE_SENTINEL);
      }

      return;
    }

    const template = templates.find(t => t.id === templateId);

    if (!template) {
      return;
    }

    setActiveLayout(parseLabelLayout(template.layout));

    // Content-dense layouts (see the built-in defaults) are designed for
    // a specific physical size and overlap badly at an arbitrary one —
    // the template's paper setup comes with its own size.
    if (!matchPaperToTemplate(template)) {
      setBaseWidthMm(customWidthMm);
      setBaseHeightMm(customHeightMm);
    }
  };

  const hasOwnSetup =
    templateSelection !== CUSTOM_TEMPLATE_SENTINEL &&
    readUserPrintSetup(user?.id, templateSelection) !== null;

  const resetToTemplateSetup = () => {
    writeUserPrintSetup(user?.id, templateSelection, null);
    applyPrintSetup(templatePrintSetup(setupTemplate));
  };

  const saveAsTemplateSetup = async () => {
    if (!selectedSavedTemplate) {
      return;
    }

    const result = await updateTemplate({
      variables: {
        id: selectedSavedTemplate.id,
        layout: JSON.stringify({
          ...withoutProductDataOverrides(parseLabelLayout(selectedSavedTemplate.layout)),
          printSetup: currentPrintSetup(),
        }),
      },
    });

    if (result.data?.labelTemplateUpdate?.productErrors.length) {
      notify({
        status: "error",
        text: intl.formatMessage(labelTemplateMessages.templateSaveFailed),
      });

      return;
    }

    writeUserPrintSetup(user?.id, templateSelection, null);
    await refetchTemplates();
    notify({
      status: "success",
      text: intl.formatMessage(messages.templateSetupSaved, { name: selectedSavedTemplate.name }),
    });
  };

  const snapshotPaper = () =>
    setPaperSnapshotBeforeEditing({
      paperId,
      customWidthMm,
      customHeightMm,
      baseWidthMm,
      baseHeightMm,
      pageFraction,
    });

  // Enters the editor seeded from a specific template (a tile's Edit or
  // Duplicate) or a brand-new blank layout (`null`, the "New template"
  // button). Never relies on templateSelection/activeLayout having already
  // been updated by a prior call in the same handler, since React doesn't
  // apply state updates until the next render.
  const startEditingTemplate = (template: (typeof templates)[number] | null, asCopy = false) => {
    const seedLayout = template ? parseLabelLayout(template.layout) : DEFAULT_LABEL_LAYOUT;

    snapshotPaper();
    setActiveLayout(seedLayout);

    if (template) {
      matchPaperToTemplate(template);
    }

    setLayoutBeforeEditing(seedLayout);
    setPreviousSelection(template ? template.id : templateSelection);
    setEditingBaseTemplateId(template && !asCopy ? template.id : null);
    setCopySourceName(template && asCopy ? template.name : null);
    setTemplateSelection(CUSTOM_TEMPLATE_SENTINEL);
    setSaveFormOpen(false);
    setIsEditing(true);
  };

  // Re-opens the editor on the unsaved layout left by "Use without saving".
  const resumeEditing = () => {
    snapshotPaper();
    setLayoutBeforeEditing(activeLayout);
    setPreviousSelection(CUSTOM_TEMPLATE_SENTINEL);
    setSaveFormOpen(false);
    setIsEditing(true);
  };

  // While creating a brand-new template (editingBaseTemplateId is null),
  // replaces the canvas with a saved template's layout as a starting point
  // to tweak, rather than the bare DEFAULT_LABEL_LAYOUT. Stays in the editor
  // and keeps editingBaseTemplateId null, so saving still creates a new
  // template rather than overwriting the source.
  const duplicateIntoEditor = (template: (typeof templates)[number]) => {
    setActiveLayout(parseLabelLayout(template.layout));
    matchPaperToTemplate(template);
  };

  // Resizing the label in the editor rewrites the layout itself (see
  // resizeLayoutForLabelSize) so the result is what actually gets saved and
  // printed, rather than a display-only trick. Always computed from the
  // layout/size the current run of resizing started from — recognised by
  // activeLayout still being exactly what the previous resize produced — so
  // typing "130" as 1 → 13 → 130 doesn't compound clamping at the tiny
  // intermediate sizes, and flipping Scale/Keep starts a fresh run.
  const resizeRunRef = useRef<{
    mode: LabelResizeMode;
    layout: LabelLayout;
    widthMm: number;
    heightMm: number;
    result: LabelLayout | null;
  } | null>(null);

  const applyEditorLabelSize = (
    nextWidthMm: number,
    nextHeightMm: number,
    mode: LabelResizeMode,
  ) => {
    let run = resizeRunRef.current;

    if (!run || run.mode !== mode || run.result !== activeLayout) {
      run = {
        mode,
        layout: activeLayout,
        widthMm: customWidthMm,
        heightMm: customHeightMm,
        result: null,
      };
      resizeRunRef.current = run;
    }

    const resized = resizeLayoutForLabelSize(
      run.layout,
      mode,
      run.widthMm,
      run.heightMm,
      nextWidthMm,
      nextHeightMm,
    );

    run.result = resized;
    setActiveLayout(resized);
    setPaperId(CUSTOM_PAPER_ID);
    setCustomKind("sheet");
    setCustomWidthMm(nextWidthMm);
    setCustomHeightMm(nextHeightMm);
    setBaseWidthMm(nextWidthMm);
    setBaseHeightMm(nextHeightMm);
    setPageFraction(null);
  };

  const cancelEditing = () => {
    setActiveLayout(layoutBeforeEditing);
    setTemplateSelection(previousSelection);

    if (paperSnapshotBeforeEditing) {
      setPaperId(paperSnapshotBeforeEditing.paperId);
      setCustomWidthMm(paperSnapshotBeforeEditing.customWidthMm);
      setCustomHeightMm(paperSnapshotBeforeEditing.customHeightMm);
      setBaseWidthMm(paperSnapshotBeforeEditing.baseWidthMm);
      setBaseHeightMm(paperSnapshotBeforeEditing.baseHeightMm);
      setPageFraction(paperSnapshotBeforeEditing.pageFraction);
    }

    setIsEditing(false);
    setSaveFormOpen(false);
  };

  const keepLayoutAndClose = () => {
    setIsEditing(false);
    setSaveFormOpen(false);
  };

  const afterTemplateSaved = async (templateId: string) => {
    if (assignToProducts) {
      await Promise.all(
        productIds.map(productId => assignTemplate({ variables: { productId, templateId } })),
      );

      if (productIds.length > 0) {
        await refetchProductTemplates();
      }
    }

    await refetchTemplates();
    setTemplateSelection(templateId);
    setSaveFormOpen(false);
    setIsEditing(false);
    setAssignToProducts(false);
    setSetAsDefault(false);
    notify({ status: "success", text: intl.formatMessage(labelTemplateMessages.templateSaved) });
  };

  const handleSaveAsNew = async () => {
    const result = await createTemplate({
      variables: {
        name: saveNameInput.trim(),
        layout: JSON.stringify(withoutProductDataOverrides(activeLayout)),
        isDefault: setAsDefault,
        defaultWidthMm: baseWidthMm ?? paper.labelWidthMm,
        defaultHeightMm: baseHeightMm ?? paper.labelHeightMm,
      },
    });
    const created = result.data?.labelTemplateCreate;

    if (created?.productErrors.length || !created?.labelTemplate) {
      notify({
        status: "error",
        text:
          created?.productErrors[0]?.message ??
          intl.formatMessage(labelTemplateMessages.templateSaveFailed),
      });

      return;
    }

    await afterTemplateSaved(created.labelTemplate.id);
  };

  const handleUpdateExisting = async () => {
    if (!editingBaseTemplateId) {
      return;
    }

    const result = await updateTemplate({
      variables: {
        id: editingBaseTemplateId,
        name: saveNameInput.trim(),
        layout: JSON.stringify(withoutProductDataOverrides(activeLayout)),
        // Unticked means "leave it as it is", so updating the current default
        // template doesn't quietly stop it being the default.
        isDefault: setAsDefault || undefined,
        defaultWidthMm: baseWidthMm ?? paper.labelWidthMm,
        defaultHeightMm: baseHeightMm ?? paper.labelHeightMm,
      },
    });
    const updated = result.data?.labelTemplateUpdate;

    if (updated?.productErrors.length || !updated?.labelTemplate) {
      notify({
        status: "error",
        text:
          updated?.productErrors[0]?.message ??
          intl.formatMessage(labelTemplateMessages.templateSaveFailed),
      });

      return;
    }

    await afterTemplateSaved(updated.labelTemplate.id);
  };

  const handleMakeDefault = async (template: { id: string }) => {
    const result = await updateTemplate({ variables: { id: template.id, isDefault: true } });

    if (result.data?.labelTemplateUpdate?.productErrors.length) {
      notify({
        status: "error",
        text: intl.formatMessage(labelTemplateMessages.templateSaveFailed),
      });

      return;
    }

    await refetchTemplates();
    notify({
      status: "success",
      text: intl.formatMessage(labelTemplateMessages.defaultTemplateUpdated),
    });
  };

  const openDelete = (template: { id: string; name: string; isDefault: boolean }) =>
    setTemplateToDelete({ id: template.id, name: template.name, isDefault: template.isDefault });

  const openRename = (template: { id: string; name: string }) => {
    setTemplateToRename(template);
    setRenameInput(template.name);
  };

  const renameTaken = templateToRename ? findTemplateNamed(renameInput, templateToRename.id) : null;
  const canRename =
    !!templateToRename &&
    !!renameInput.trim() &&
    !renameTaken &&
    renameInput.trim() !== templateToRename.name;

  const handleRenameTemplate = async () => {
    if (!templateToRename || !canRename) {
      return;
    }

    const result = await updateTemplate({
      variables: { id: templateToRename.id, name: renameInput.trim() },
    });
    const renamed = result.data?.labelTemplateUpdate;

    if (renamed?.productErrors.length || !renamed?.labelTemplate) {
      notify({
        status: "error",
        text:
          renamed?.productErrors[0]?.message ??
          intl.formatMessage(labelTemplateMessages.templateSaveFailed),
      });

      return;
    }

    await refetchTemplates();
    setTemplateToRename(null);
    notify({ status: "success", text: intl.formatMessage(labelTemplateMessages.templateRenamed) });
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) {
      return;
    }

    const result = await deleteTemplate({ variables: { id: templateToDelete.id } });

    if (result.data?.labelTemplateDelete?.productErrors.length) {
      notify({
        status: "error",
        text: intl.formatMessage(labelTemplateMessages.templateDeleteFailed),
      });

      return;
    }

    if (templateSelection === templateToDelete.id) {
      setTemplateSelection(AUTO_TEMPLATE_SENTINEL);
    }

    setTemplateToDelete(null);
    await refetchTemplates();

    if (productIds.length > 0) {
      await refetchProductTemplates();
    }

    notify({ status: "success", text: intl.formatMessage(labelTemplateMessages.templateDeleted) });
  };

  // "Save as new" must never silently reuse a name: it needs a non-empty name
  // no other template has (including the one being edited, whose name the
  // form is pre-filled with for "Update"). "Update" only rejects names used
  // by OTHER templates.
  const trimmedSaveName = saveNameInput.trim();
  const saveNameTakenBy = findTemplateNamed(saveNameInput);
  const updateNameTakenBy = findTemplateNamed(saveNameInput, editingBaseTemplateId);
  const canSaveAsNew = !!trimmedSaveName && !saveNameTakenBy;
  const canUpdateExisting = !!trimmedSaveName && !updateNameTakenBy;
  const saveNameHelper = !trimmedSaveName
    ? intl.formatMessage(labelTemplateMessages.templateNameRequired)
    : editingBaseTemplateId && saveNameTakenBy?.id === editingBaseTemplateId
      ? intl.formatMessage(labelTemplateMessages.saveAsNewNeedsNewName)
      : saveNameTakenBy
        ? intl.formatMessage(labelTemplateMessages.templateNameTaken, {
            name: saveNameTakenBy.name,
          })
        : undefined;

  const toggleSaveForm = () => {
    if (isSaveFormOpen) {
      setSaveFormOpen(false);

      return;
    }

    const base = editingBaseTemplate
      ? editingBaseTemplate.name
      : copySourceName
        ? intl.formatMessage(labelTemplateMessages.templateCopyName, { name: copySourceName })
        : "";

    setSaveNameInput(base);
    // Opt-in every time: making a template everyone's default is a deliberate
    // choice, not something to carry over unnoticed.
    setSetAsDefault(false);
    setSaveFormOpen(true);
  };

  // The page is its own scroll area (see .labelPageScroller), as tall as the
  // space left under whatever sits above it: nothing in the full dashboard,
  // the "MAXCEI · Operações" bar on ops. A fixed screen height left the
  // bottom of the page unreachable below that bar.
  const pageScrollerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const scroller = pageScrollerRef.current;

    if (!scroller) {
      return;
    }

    const fillViewport = () => {
      const top = scroller.getBoundingClientRect().top + window.scrollY;

      // 1px: the save-bar anchor's border below every dashboard page.
      scroller.style.height = `${Math.max(320, window.innerHeight - top - 1)}px`;
    };

    fillViewport();
    window.addEventListener("resize", fillViewport);

    return () => window.removeEventListener("resize", fillViewport);
  }, [isEditing]);

  // The editor covers the page; keep the page underneath from scrolling
  // along with it.
  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isEditing]);

  const renderThumbnail = (layout: LabelLayout, widthMm: number, heightMm: number) => (
    <Box
      __width={`min(100%, ${(TILE_THUMB_HEIGHT_PX * widthMm) / heightMm}px)`}
      style={{ pointerEvents: "none" }}
    >
      <LabelPreviewCard
        item={sampleItem}
        layout={applyProductMarks(layout, productMarksMap.get(sampleItem.productId) ?? null)}
        widthMm={widthMm}
        heightMm={heightMm}
        fit
      />
    </Box>
  );

  const renderTile = (options: {
    key: string;
    name: string;
    subtitle: ReactNode;
    selected: boolean;
    onSelect: () => void;
    thumbnail: ReactNode;
    tools?: ReactNode;
    testId?: string;
  }) => (
    <Box
      key={options.key}
      className="labelTemplateTile"
      data-selected={options.selected}
      data-test-id={options.testId}
      role="button"
      tabIndex={0}
      aria-pressed={options.selected}
      onClick={options.onSelect}
      onKeyDown={event => {
        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          options.onSelect();
        }
      }}
      position="relative"
      display="flex"
      flexDirection="column"
      gap={1.5}
      padding={2}
      borderWidth={1}
      borderStyle="solid"
      borderColor="default1"
      borderRadius={3}
      backgroundColor="default1"
      style={{ minWidth: 0 }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        backgroundColor="default2"
        borderRadius={2}
        padding={1.5}
        overflow="hidden"
        __height={`${TILE_THUMB_HEIGHT_PX + 12}px`}
      >
        {options.thumbnail}
      </Box>
      {options.selected && (
        <Box
          position="absolute"
          display="grid"
          placeItems="center"
          borderRadius="100%"
          backgroundColor="accent1"
          color="default1"
          __top="12px"
          __left="12px"
          __width="20px"
          __height="20px"
          aria-hidden
        >
          <Check size={12} strokeWidth={3} color="white" />
        </Box>
      )}
      <Text
        size={1}
        fontWeight="medium"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          minHeight: "2.7em",
          lineHeight: 1.35,
        }}
      >
        {options.name}
      </Text>
      <Text size={1} color="default2" display="flex" alignItems="center" gap={1}>
        {options.subtitle}
      </Text>
      {options.tools && (
        <Box
          className="labelTemplateTileTools"
          position="absolute"
          display="flex"
          gap={1}
          __top="12px"
          __right="12px"
          onClick={stopPropagation}
          onKeyDown={stopPropagation}
        >
          {options.tools}
        </Box>
      )}
    </Box>
  );

  const tileToolButtonProps = {
    variant: "secondary" as const,
    size: "small" as const,
    style: { width: 28, height: 28, padding: 0 },
  };

  const renderTemplateTools = (template: (typeof templates)[number]) => {
    if (!canManageTemplates) {
      return undefined;
    }

    const menuItems: Array<{ label: string; onClick: () => void; testId: string }> = [
      {
        label: intl.formatMessage(labelTemplateMessages.galleryDuplicate),
        onClick: () => startEditingTemplate(template, true),
        testId: "duplicate",
      },
      ...(template.canEdit
        ? [
            {
              label: intl.formatMessage(labelTemplateMessages.galleryRename),
              onClick: () => openRename(template),
              testId: "rename",
            },
          ]
        : []),
      ...(template.canSetDefault && !template.isDefault
        ? [
            {
              label: intl.formatMessage(labelTemplateMessages.makeDefault),
              onClick: () => handleMakeDefault(template),
              testId: "make-default",
            },
          ]
        : []),
      ...(template.canEdit
        ? [
            {
              label: intl.formatMessage(labelTemplateMessages.galleryDelete),
              onClick: () => openDelete(template),
              testId: "delete",
            },
          ]
        : []),
    ];

    return (
      <>
        <Button
          {...tileToolButtonProps}
          icon={<Pencil size={14} />}
          aria-label={intl.formatMessage(labelTemplateMessages.editTemplateNamed, {
            name: template.name,
          })}
          title={intl.formatMessage(
            template.canEdit
              ? labelTemplateMessages.galleryEdit
              : labelTemplateMessages.editCopyHint,
          )}
          // A shared template opens as a personal copy: saving creates the
          // user's own template and leaves the shared one unchanged.
          onClick={() => startEditingTemplate(template, !template.canEdit)}
          data-test-id="template-edit"
        />
        <Dropdown>
          <Dropdown.Trigger>
            <Button
              {...tileToolButtonProps}
              icon={<Ellipsis size={14} />}
              aria-label={intl.formatMessage(labelTemplateMessages.moreActions)}
              title={intl.formatMessage(labelTemplateMessages.moreActions)}
              data-test-id="template-menu"
            />
          </Dropdown.Trigger>
          <Dropdown.Content align="end">
            <List
              padding={1}
              borderRadius={4}
              boxShadow="defaultOverlay"
              backgroundColor="default1"
            >
              {menuItems.map(menuItem => (
                <Dropdown.Item key={menuItem.testId}>
                  <List.Item
                    borderRadius={3}
                    paddingX={2}
                    paddingY={1.5}
                    onClick={menuItem.onClick}
                    data-test-id={`template-${menuItem.testId}`}
                  >
                    <Text size={2}>{menuItem.label}</Text>
                  </List.Item>
                </Dropdown.Item>
              ))}
            </List>
          </Dropdown.Content>
        </Dropdown>
      </>
    );
  };

  const templateSizeText = (template: (typeof templates)[number]) =>
    template.defaultWidthMm && template.defaultHeightMm
      ? `${template.defaultWidthMm}×${template.defaultHeightMm} mm`
      : template.createdByName
        ? intl.formatMessage(labelTemplateMessages.galleryCreatedBy, {
            name: template.createdByName,
          })
        : "";

  const templateThumbnail = (template: (typeof templates)[number]) =>
    renderThumbnail(
      parseLabelLayout(template.layout),
      template.defaultWidthMm ?? paper.labelWidthMm,
      template.defaultHeightMm ?? paper.labelHeightMm,
    );

  const templateGrid = (
    <Box
      display="grid"
      gap={2.5}
      __gridTemplateColumns="repeat(auto-fill, minmax(140px, 1fr))"
      data-test-id="template-tiles"
    >
      {renderTile({
        key: AUTO_TEMPLATE_SENTINEL,
        name: intl.formatMessage(labelTemplateMessages.autoTemplateName),
        subtitle: intl.formatMessage(labelTemplateMessages.autoTemplateHint),
        selected: templateSelection === AUTO_TEMPLATE_SENTINEL,
        onSelect: () => selectTemplate(AUTO_TEMPLATE_SENTINEL),
        thumbnail: <Wand2 size={24} aria-hidden />,
        testId: "template-auto",
      })}
      {templateSelection === CUSTOM_TEMPLATE_SENTINEL &&
        renderTile({
          key: CUSTOM_TEMPLATE_SENTINEL,
          name: customTemplateName,
          subtitle: `${paper.labelWidthMm}×${paper.labelHeightMm} mm`,
          selected: true,
          onSelect: () => undefined,
          thumbnail: renderThumbnail(activeLayout, paper.labelWidthMm, paper.labelHeightMm),
          tools: canManageTemplates ? (
            <Button
              {...tileToolButtonProps}
              icon={<Pencil size={14} />}
              aria-label={intl.formatMessage(labelTemplateMessages.editLayout)}
              title={intl.formatMessage(labelTemplateMessages.editLayout)}
              onClick={resumeEditing}
            />
          ) : undefined,
        })}
      {templates.map(template =>
        renderTile({
          key: template.id,
          name: template.name,
          subtitle: (
            <>
              {templateSizeText(template)}
              {template.isDefault && (
                <Box
                  as="span"
                  display="inline-flex"
                  title={intl.formatMessage(labelTemplateMessages.galleryDefaultBadge)}
                  aria-label={intl.formatMessage(labelTemplateMessages.galleryDefaultBadge)}
                >
                  <Star size={12} fill="currentColor" aria-hidden />
                </Box>
              )}
            </>
          ),
          selected: templateSelection === template.id,
          onSelect: () => selectTemplate(template.id),
          thumbnail: templateThumbnail(template),
          tools: renderTemplateTools(template),
          testId: "template-tile",
        }),
      )}
    </Box>
  );

  // The browser can't read or set the OS printer's paper size — the print
  // dialog gets whatever the @page rule below declares, and the user has to
  // pick the same size in the system dialog. So the exact numbers that rule
  // uses are shown next to the paper select (same source as the rule
  // itself, so they can't drift apart), with a one-click copy for typing
  // into the system's "Manage Custom Sizes".
  const printPageWidthMm = paper.kind === "sheet" ? (paper.pageWidthMm ?? 0) : rollOutputWidthMm;
  const printPageHeightMm = paper.kind === "sheet" ? (paper.pageHeightMm ?? 0) : rollPageHeightMm;
  const printPageSizeText = `${printPageWidthMm} × ${printPageHeightMm} mm`;

  const copyPageSize = () => {
    navigator.clipboard
      ?.writeText(`${printPageWidthMm} x ${printPageHeightMm}`)
      .then(() =>
        notify({
          status: "success",
          text: intl.formatMessage(messages.printPageSizeCopied, { size: printPageSizeText }),
        }),
      )
      .catch(() => undefined);
  };

  const adjustSettings = (
    <Box display="flex" flexDirection="column" gap={4} paddingTop={2}>
      <Box display="flex" flexDirection="column" gap={2}>
        <Text size={1} fontWeight="bold">
          {intl.formatMessage(messages.snapshotControlsLabel)}
        </Text>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="secondary"
            size="small"
            onClick={() => setRotationDegrees(value => ((value + 270) % 360) as 0 | 90 | 180 | 270)}
          >
            {intl.formatMessage(messages.rotateLeft)}
          </Button>
          <Button
            variant="secondary"
            size="small"
            onClick={() => setRotationDegrees(value => ((value + 90) % 360) as 0 | 90 | 180 | 270)}
          >
            {intl.formatMessage(messages.rotateRight)}
          </Button>
          <Button
            variant={cropToFill ? "primary" : "secondary"}
            size="small"
            onClick={() => setCropToFill(value => !value)}
          >
            {intl.formatMessage(cropToFill ? messages.fitWholeDesign : messages.cropToFill)}
          </Button>
        </Box>
        <label style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Text size={1}>{intl.formatMessage(messages.zoomLabel, { zoom: zoomPercent })}</Text>
          <input
            aria-label={intl.formatMessage(messages.zoomLabel, { zoom: zoomPercent })}
            type="range"
            min={25}
            max={200}
            step={1}
            value={zoomPercent}
            onChange={event => setZoomPercent(Number(event.target.value))}
            style={{ flex: 1, minWidth: 140 }}
          />
        </label>
      </Box>

      {paper.kind === "roll" && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Text size={1} fontWeight="bold">
            {intl.formatMessage(messages.rollLayoutLabel)}
          </Text>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Input
              label={intl.formatMessage(messages.rollColumnsLabel)}
              type="number"
              min={1}
              max={12}
              size="small"
              value={rollColumns}
              onChange={event =>
                setRollColumns(Math.min(12, Math.max(1, Number(event.target.value) || 1)))
              }
            />
            <Input
              label={intl.formatMessage(messages.rollRowsPerPageLabel)}
              type="number"
              min={1}
              max={20}
              size="small"
              value={rollRowsPerPage}
              onChange={event =>
                setRollRowsPerPage(Math.min(20, Math.max(1, Number(event.target.value) || 1)))
              }
            />
            <MillimeterInput
              label={intl.formatMessage(messages.rollLeftMarginLabel)}
              value={rollLeftMarginMm}
              onChange={setRollLeftMarginMm}
            />
            <MillimeterInput
              label={intl.formatMessage(messages.rollRightMarginLabel)}
              value={rollRightMarginMm}
              onChange={setRollRightMarginMm}
            />
            <MillimeterInput
              label={intl.formatMessage(messages.rollMiddleGapLabel)}
              value={rollMiddleGapMm}
              onChange={setRollMiddleGapMm}
            />
            <MillimeterInput
              label={intl.formatMessage(messages.rollRowGapLabel)}
              value={rollRowGapMm}
              onChange={setRollRowGapMm}
            />
          </Box>
          <Text size={1} color="default2">
            {intl.formatMessage(messages.rollOutputWidth, {
              width: rollOutputWidthMm,
              height: rollPageHeightMm,
            })}
          </Text>
          <Text size={1} color="default2">
            {intl.formatMessage(messages.rollPrintSettingsHint)}
          </Text>
        </Box>
      )}

      {isCustom && (
        <Box display="flex" flexDirection="column" gap={3}>
          <Text size={1} fontWeight="bold">
            {intl.formatMessage(messages.customKindLabel)}
          </Text>
          <Box display="flex" gap={2}>
            <Button
              variant={customKind === "sheet" ? "primary" : "secondary"}
              size="small"
              onClick={() => setCustomKind("sheet")}
            >
              {intl.formatMessage(messages.customKindSheet)}
            </Button>
            <Button
              variant={customKind === "roll" ? "primary" : "secondary"}
              size="small"
              onClick={() => setCustomKind("roll")}
            >
              {intl.formatMessage(messages.customKindRoll)}
            </Button>
          </Box>
          {customKind === "sheet" && (
            <>
              <Select
                label={intl.formatMessage(messages.customPageLabel)}
                options={sheetPageOptions}
                value={sheetPageOptions.find(option => option.value === customPageId) ?? null}
                onChange={option => setCustomPageId(option.value)}
                size="small"
              />
              <Box display="flex" flexDirection="column" gap={1}>
                <Text size={1} color="default2">
                  {intl.formatMessage(messages.perPageFractionLabel)}
                </Text>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {PAGE_FRACTION_PRESETS.map(({ columns, rows }) => (
                    <Button
                      key={`${columns}x${rows}`}
                      variant="secondary"
                      size="small"
                      onClick={() => {
                        setPageFraction({ columns, rows });

                        const cell = fractionSizeMm(
                          customPage.pageWidthMm ?? 0,
                          customPage.pageHeightMm ?? 0,
                          columns,
                          rows,
                        );

                        setCustomWidthMm(Math.round(cell.widthMm * 10) / 10);
                        setCustomHeightMm(Math.round(cell.heightMm * 10) / 10);
                      }}
                    >
                      {intl.formatMessage(messages.perPageFraction, { count: columns * rows })}
                    </Button>
                  ))}
                </Box>
              </Box>
            </>
          )}
          <Box display="flex" gap={3}>
            <Input
              label={intl.formatMessage(messages.customWidthLabel)}
              type="number"
              min={5}
              max={300}
              size="small"
              value={customWidthMm}
              onChange={e => {
                const next = Math.max(5, Number(e.target.value) || 5);

                setCustomWidthMm(next);
                setBaseWidthMm(next);
                setBaseHeightMm(customHeightMm);
                setPageFraction(null);
              }}
            />
            <Input
              label={intl.formatMessage(messages.customHeightLabel)}
              type="number"
              min={5}
              max={300}
              size="small"
              value={customHeightMm}
              onChange={e => {
                // Typing a height directly is redesigning the label's own
                // size (unlike a quick-size preset, which keeps the design
                // and just prints more per page), so it resets the
                // font-scaling reference too.
                const next = Math.max(5, Number(e.target.value) || 5);

                setCustomHeightMm(next);
                setBaseWidthMm(customWidthMm);
                setBaseHeightMm(next);
                setPageFraction(null);
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );

  const paperRow = (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      padding={3}
      borderRadius={3}
      backgroundColor="default2"
    >
      <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
        <Box __flex="1 1 240px" __minWidth="200px">
          <Select
            label={intl.formatMessage(messages.paperLabel)}
            options={paperOptions}
            value={paperOptions.find(option => option.value === paperId) ?? null}
            onChange={option => setPaperId(option.value)}
            size="small"
          />
        </Box>
        <Button
          variant={isAdjustOpen ? "primary" : "secondary"}
          size="small"
          icon={<SlidersHorizontal size={14} />}
          aria-expanded={isAdjustOpen}
          onClick={() => setAdjustOpen(value => !value)}
        >
          {intl.formatMessage(messages.paperAdjust)}
        </Button>
      </Box>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Text size={1} color="default2" style={{ flex: "1 1 220px" }}>
          {intl.formatMessage(messages.printPageSizeSummary, { size: printPageSizeText })}
        </Text>
        <Button variant="tertiary" size="small" icon={<Copy size={14} />} onClick={copyPageSize}>
          {intl.formatMessage(messages.printPageSizeCopy)}
        </Button>
        {onOpenHelp && paper.kind === "roll" && (
          <Button
            variant="tertiary"
            size="small"
            icon={<CircleHelp size={14} />}
            onClick={onOpenHelp}
          >
            {intl.formatMessage(helpMessages.openHelp)}
          </Button>
        )}
      </Box>
      {(hasOwnSetup || selectedSavedTemplate?.canEdit) && (
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          {hasOwnSetup && (
            <Text size={1} color="default2" style={{ flex: "1 1 220px" }}>
              {intl.formatMessage(messages.ownSetupNote)}
            </Text>
          )}
          {hasOwnSetup && (
            <Button variant="tertiary" size="small" onClick={resetToTemplateSetup}>
              {intl.formatMessage(messages.templateSetupReset)}
            </Button>
          )}
          {selectedSavedTemplate?.canEdit && (
            <Button
              variant="secondary"
              size="small"
              disabled={updateTemplateOpts.loading}
              onClick={saveAsTemplateSetup}
              data-test-id="save-template-setup"
            >
              {intl.formatMessage(messages.templateSetupSave)}
            </Button>
          )}
        </Box>
      )}
      {isAdjustOpen && adjustSettings}
    </Box>
  );

  const labelsPerPreviewPage =
    paper.kind === "sheet" ? (sheetGrid?.perPage ?? 1) : rollLabelsPerPage;
  const previewPageCount = Math.max(1, Math.ceil(items.length / labelsPerPreviewPage));
  const shownPreviewPage = Math.min(previewPage, previewPageCount - 1);
  const previewItems = items.slice(
    shownPreviewPage * labelsPerPreviewPage,
    (shownPreviewPage + 1) * labelsPerPreviewPage,
  );

  const previewCard = (
    <DashboardCard
      borderWidth={1}
      borderStyle="solid"
      borderColor="default1"
      borderRadius={4}
      paddingBottom={4}
    >
      <DashboardCard.Header>
        <DashboardCard.Title>
          {intl.formatMessage(messages.previewTitle, { count: items.length })}
        </DashboardCard.Title>
        {previewPageCount > 1 && (
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant="tertiary"
              size="small"
              icon={<ChevronLeft size={16} />}
              aria-label={intl.formatMessage(messages.previewPreviousPage)}
              disabled={shownPreviewPage === 0}
              onClick={() => setPreviewPage(shownPreviewPage - 1)}
            />
            <Text size={2} style={{ fontVariantNumeric: "tabular-nums" }}>
              {shownPreviewPage + 1}/{previewPageCount}
            </Text>
            <Button
              variant="tertiary"
              size="small"
              icon={<ChevronRight size={16} />}
              aria-label={intl.formatMessage(messages.previewNextPage)}
              disabled={shownPreviewPage >= previewPageCount - 1}
              onClick={() => setPreviewPage(shownPreviewPage + 1)}
            />
          </Box>
        )}
      </DashboardCard.Header>
      <DashboardCard.Content display="flex" flexDirection="column" gap={3}>
        {paper.kind === "sheet" && sheetGrid && items.length > 0 && (
          <Text size={1} color="default2">
            {intl.formatMessage(messages.perPageCaption, {
              perPage: sheetGrid.perPage,
              columns: sheetGrid.columns,
              rows: sheetGrid.rows,
              pages: previewPageCount,
            })}
          </Text>
        )}
        {items.length === 0 ? (
          <Text size={2} color="default2" textAlign="center" padding={6}>
            {intl.formatMessage(messages.emptyPreview)}
          </Text>
        ) : (
          <Box
            className="labelPreviewLabels"
            padding={4}
            borderRadius={3}
            backgroundColor="default2"
          >
            <Box style={previewPageStyle}>
              {previewItems.map((item, index) => {
                const size = resolveItemPrintSize(item);
                const design = resolveItemDesignSize(item);

                return (
                  <Box
                    key={`${item.id}-${shownPreviewPage}-${index}`}
                    style={{ width: previewLabelWidth(size.widthMm) }}
                  >
                    <LabelPreviewCard
                      item={item}
                      layout={resolveItemLayout(item)}
                      widthMm={size.widthMm}
                      heightMm={size.heightMm}
                      designWidthMm={design.widthMm}
                      designHeightMm={design.heightMm}
                      rotationDegrees={rotationDegrees}
                      zoomPercent={zoomPercent}
                      crop={cropToFill}
                      fit
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
        <Text size={1} color="default2">
          {intl.formatMessage(messages.printNote)}
        </Text>
      </DashboardCard.Content>
    </DashboardCard>
  );

  const templateCard = (
    <DashboardCard
      borderWidth={1}
      borderStyle="solid"
      borderColor="default1"
      borderRadius={4}
      paddingBottom={4}
    >
      <DashboardCard.Header>
        <DashboardCard.Title>
          {intl.formatMessage(labelTemplateMessages.templateLabel)}
        </DashboardCard.Title>
        {canManageTemplates && (
          <Button
            variant="tertiary"
            size="small"
            icon={<Plus size={14} />}
            onClick={() => startEditingTemplate(null)}
            data-test-id="new-template"
          >
            {intl.formatMessage(labelTemplateMessages.newTemplate)}
          </Button>
        )}
      </DashboardCard.Header>
      <DashboardCard.Content display="flex" flexDirection="column" gap={4}>
        {templateGrid}
        {templates.length === 0 && (
          <Text size={1} color="default2">
            {intl.formatMessage(labelTemplateMessages.galleryEmpty)}
          </Text>
        )}
        {paperRow}
      </DashboardCard.Content>
    </DashboardCard>
  );

  const pillProps = {
    size: 1 as const,
    color: "default2" as const,
    borderWidth: 1 as const,
    borderStyle: "solid" as const,
    borderColor: "default1" as const,
    backgroundColor: "default2" as const,
    borderRadius: 7 as const,
    paddingX: 2.5 as const,
    paddingY: 0.5 as const,
    whiteSpace: "nowrap" as const,
  };

  // Stays on top while the page scrolls, so Print/Back are always reachable
  // (they used to sit at the top of a long page, or not at all once in the
  // editor). The pills say what will print without scrolling to check.
  const header = (
    <TopNav
      title={title}
      style={{ position: "sticky", top: 0, zIndex: 3, height: "auto", minHeight: 72 }}
    >
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" justifyContent="flex-end">
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" marginRight={2}>
          <Text {...pillProps} color="default1" fontWeight="bold">
            {intl.formatMessage(messages.labelsCount, { count: items.length })}
          </Text>
          <Text {...pillProps}>{selectedTemplateName}</Text>
          <Text {...pillProps}>{printPageSizeText}</Text>
        </Box>
        {onOpenHelp && (
          <Button
            variant="tertiary"
            icon={<CircleHelp size={16} />}
            onClick={onOpenHelp}
            data-test-id="printing-tips"
          >
            {intl.formatMessage(helpMessages.title)}
          </Button>
        )}
        {onBack && (
          <Button
            variant="secondary"
            icon={<ArrowLeft size={16} />}
            onClick={onBack}
            data-test-id="back"
          >
            {intl.formatMessage(messages.back)}
          </Button>
        )}
        <Button
          variant="primary"
          icon={<Printer size={16} />}
          disabled={items.length === 0}
          onClick={handlePrint}
          data-test-id="print"
        >
          {intl.formatMessage(messages.printCount, { count: items.length })}
        </Button>
      </Box>
    </TopNav>
  );

  const isLayoutChanged = activeLayout !== layoutBeforeEditing;

  const editor = (
    <Box
      position="fixed"
      inset={0}
      display="flex"
      flexDirection="column"
      backgroundColor="default1"
      role="dialog"
      aria-modal="true"
      aria-label={
        editingBaseTemplate
          ? intl.formatMessage(labelTemplateMessages.editTemplateNamed, {
              name: editingBaseTemplate.name,
            })
          : intl.formatMessage(labelTemplateMessages.newTemplate)
      }
      __zIndex="10"
      data-test-id="label-editor"
    >
      <Box
        display="flex"
        alignItems="center"
        gap={3}
        flexWrap="wrap"
        paddingX={6}
        paddingY={3}
        borderBottomWidth={1}
        borderBottomStyle="solid"
        borderColor="default1"
        position="relative"
      >
        <Button variant="tertiary" icon={<ArrowLeft size={16} />} onClick={keepLayoutAndClose}>
          {intl.formatMessage(labelTemplateMessages.useWithoutSaving)}
        </Button>
        <Text size={5} fontWeight="bold" style={{ minWidth: 0 }}>
          {editingBaseTemplate
            ? intl.formatMessage(labelTemplateMessages.editTemplateNamed, {
                name: editingBaseTemplate.name,
              })
            : copySourceName
              ? intl.formatMessage(labelTemplateMessages.templateCopyName, {
                  name: copySourceName,
                })
              : intl.formatMessage(labelTemplateMessages.newTemplate)}
        </Text>
        {copySourceName && (
          <Text size={1} color="default2">
            {intl.formatMessage(labelTemplateMessages.editingCopyNote, { name: copySourceName })}
          </Text>
        )}
        {isLayoutChanged && (
          <Text
            size={1}
            color="warning1"
            backgroundColor="warning1"
            paddingX={2}
            paddingY={0.5}
            borderRadius={7}
          >
            {intl.formatMessage(labelTemplateMessages.unsavedChanges)}
          </Text>
        )}
        <Box marginLeft="auto" display="flex" gap={2}>
          <Button variant="secondary" onClick={cancelEditing}>
            {intl.formatMessage(labelTemplateMessages.cancelEditing)}
          </Button>
          <Button
            variant="primary"
            aria-expanded={isSaveFormOpen}
            onClick={toggleSaveForm}
            data-test-id="save-template"
          >
            {intl.formatMessage(labelTemplateMessages.saveConfirm)}
            <ChevronDown size={16} />
          </Button>
        </Box>
        {isSaveFormOpen && (
          <Box
            position="absolute"
            display="flex"
            flexDirection="column"
            gap={3}
            padding={4}
            borderWidth={1}
            borderStyle="solid"
            borderColor="default1"
            borderRadius={4}
            backgroundColor="default1"
            boxShadow="defaultOverlay"
            __top="calc(100% + 8px)"
            __right="24px"
            __width="min(380px, calc(100vw - 32px))"
            __zIndex="1"
            data-test-id="save-template-form"
          >
            <Input
              label={intl.formatMessage(labelTemplateMessages.templateNameLabel)}
              size="small"
              autoFocus
              value={saveNameInput}
              onChange={e => setSaveNameInput(e.target.value)}
              error={!canSaveAsNew && !canUpdateExisting}
              helperText={saveNameHelper}
            />
            {canAssignTemplates && (
              <Checkbox
                checked={assignToProducts}
                onCheckedChange={(checked: CheckedState) => setAssignToProducts(checked === true)}
              >
                <Text size={2}>
                  {intl.formatMessage(labelTemplateMessages.assignToSelected, {
                    count: productIds.length,
                  })}
                </Text>
              </Checkbox>
            )}
            {templates.some(template => template.canSetDefault) && (
              <Checkbox
                checked={setAsDefault}
                onCheckedChange={(checked: CheckedState) => setSetAsDefault(checked === true)}
              >
                <Text size={2}>{intl.formatMessage(labelTemplateMessages.setAsDefaultLabel)}</Text>
              </Checkbox>
            )}
            <Box display="flex" gap={2} flexWrap="wrap">
              {editingBaseTemplate?.canEdit && (
                <Button
                  variant="primary"
                  disabled={!canUpdateExisting || updateTemplateOpts.loading}
                  onClick={handleUpdateExisting}
                >
                  {intl.formatMessage(labelTemplateMessages.updateTemplate, {
                    name: editingBaseTemplate.name,
                  })}
                </Button>
              )}
              <Button
                variant={editingBaseTemplate?.canEdit ? "secondary" : "primary"}
                disabled={!canSaveAsNew || createTemplateOpts.loading}
                onClick={handleSaveAsNew}
              >
                {intl.formatMessage(labelTemplateMessages.saveAsNewTemplate)}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        gap={4}
        paddingX={6}
        paddingY={5}
        overflowY="auto"
        __flex="1"
        onClick={() => setSaveFormOpen(false)}
      >
        <Box display="flex" gap={3} flexWrap="wrap" alignItems="flex-start">
          <Input
            label={intl.formatMessage(messages.customWidthLabel)}
            type="number"
            min={5}
            max={300}
            size="small"
            value={customWidthMm}
            onChange={e =>
              applyEditorLabelSize(
                Math.max(5, Number(e.target.value) || 5),
                customHeightMm,
                scaleContentOnResize ? "scale" : "keep",
              )
            }
          />
          <Input
            label={intl.formatMessage(messages.customHeightLabel)}
            type="number"
            min={5}
            max={300}
            size="small"
            value={customHeightMm}
            onChange={e =>
              applyEditorLabelSize(
                customWidthMm,
                Math.max(5, Number(e.target.value) || 5),
                scaleContentOnResize ? "scale" : "keep",
              )
            }
          />
          <Box display="flex" flexDirection="column" gap={1}>
            <Text size={1} color="default2">
              {intl.formatMessage(messages.resizeContentModeLabel)}
            </Text>
            <Box display="flex" gap={1}>
              <Button
                variant={scaleContentOnResize ? "primary" : "secondary"}
                size="small"
                onClick={() => setScaleContentOnResize(true)}
              >
                {intl.formatMessage(messages.resizeContentModeScale)}
              </Button>
              <Button
                variant={!scaleContentOnResize ? "primary" : "secondary"}
                size="small"
                onClick={() => setScaleContentOnResize(false)}
              >
                {intl.formatMessage(messages.resizeContentModeKeep)}
              </Button>
            </Box>
            <Text size={1} color="default2" style={{ maxWidth: 360 }}>
              {intl.formatMessage(messages.resizeContentModeHint)}
            </Text>
          </Box>
        </Box>
        <LabelCanvasEditor
          item={sampleItem}
          layout={activeLayout}
          widthMm={paper.labelWidthMm}
          heightMm={paper.labelHeightMm}
          onChange={setActiveLayout}
        />
        {!editingBaseTemplateId && templates.length > 0 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Text size={2} fontWeight="bold">
              {intl.formatMessage(labelTemplateMessages.duplicateFromTitle)}
            </Text>
            <Box
              display="grid"
              gap={2.5}
              __gridTemplateColumns="repeat(auto-fill, minmax(140px, 1fr))"
            >
              {templates.map(template =>
                renderTile({
                  key: template.id,
                  name: template.name,
                  subtitle: templateSizeText(template),
                  selected: false,
                  onSelect: () => duplicateIntoEditor(template),
                  thumbnail: templateThumbnail(template),
                }),
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {isEditing ? (
        editor
      ) : (
        <Box
          ref={pageScrollerRef}
          className="labelPageScroller"
          display="flex"
          flexDirection="column"
        >
          {header}
          <Box
            display="flex"
            flexDirection="column"
            gap={5}
            paddingX={6}
            paddingTop={5}
            // Room below the last card: macaw's Select always opens its option
            // list downward, so the paper select at the bottom needs space
            // under it, or the list ran off screen with nothing left to scroll.
            __paddingBottom="min(320px, 40vh)"
          >
            {topContent}
            {/* Template and preview side by side, the preview pinned while the
                template list scrolls, so picking a template shows its result
                without scrolling down to check. */}
            <Box className="labelWorkspaceGrid">
              <Box style={{ minWidth: 0 }}>{templateCard}</Box>
              <Box className="labelPreviewColumn">{previewCard}</Box>
            </Box>
          </Box>
        </Box>
      )}

      {templateToRename && (
        <DashboardModal onChange={() => setTemplateToRename(null)} open>
          <DashboardModal.Content size="sm">
            <DashboardModal.Header>
              {intl.formatMessage(labelTemplateMessages.renameTemplateTitle)}
            </DashboardModal.Header>
            <Input
              label={intl.formatMessage(labelTemplateMessages.templateNameLabel)}
              value={renameInput}
              autoFocus
              onChange={e => setRenameInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  handleRenameTemplate();
                }
              }}
              error={!renameInput.trim() || !!renameTaken}
              helperText={
                !renameInput.trim()
                  ? intl.formatMessage(labelTemplateMessages.templateNameRequired)
                  : renameTaken
                    ? intl.formatMessage(labelTemplateMessages.templateNameTaken, {
                        name: renameTaken.name,
                      })
                    : undefined
              }
            />
            <DashboardModal.Actions>
              <Button variant="secondary" onClick={() => setTemplateToRename(null)}>
                {intl.formatMessage(messages.cancel)}
              </Button>
              <Button
                variant="primary"
                disabled={!canRename || updateTemplateOpts.loading}
                onClick={handleRenameTemplate}
              >
                {intl.formatMessage(labelTemplateMessages.galleryRename)}
              </Button>
            </DashboardModal.Actions>
          </DashboardModal.Content>
        </DashboardModal>
      )}

      {templateToDelete && (
        <DashboardModal onChange={() => setTemplateToDelete(null)} open>
          <DashboardModal.Content size="sm">
            <DashboardModal.Header>
              {intl.formatMessage(labelTemplateMessages.deleteTemplateTitle)}
            </DashboardModal.Header>
            <Text size={3}>
              {intl.formatMessage(
                templateToDelete.isDefault
                  ? labelTemplateMessages.deleteDefaultTemplateBody
                  : labelTemplateMessages.deleteTemplateBody,
                { name: templateToDelete.name },
              )}
            </Text>
            <DashboardModal.Actions>
              <Button variant="secondary" onClick={() => setTemplateToDelete(null)}>
                {intl.formatMessage(messages.cancel)}
              </Button>
              <Button
                variant="primary"
                disabled={deleteTemplateOpts.loading}
                onClick={handleDeleteTemplate}
              >
                {intl.formatMessage(labelTemplateMessages.galleryDelete)}
              </Button>
            </DashboardModal.Actions>
          </DashboardModal.Content>
        </DashboardModal>
      )}

      {createPortal(
        // Thermal (roll) printers only make black dots: colours and greys
        // come out as faint speckle, so that print copy is pure black (see
        // labelPrint.css).
        <div className="labelPrintPortalRoot" data-thermal={paper.kind === "roll"}>
          <style>{pageSizeRule}</style>
          {paper.kind === "sheet"
            ? Array.from(
                { length: Math.max(1, Math.ceil(items.length / (sheetGrid?.perPage ?? 1))) },
                (_unused, pageIndex) => {
                  const pageItems = items.slice(
                    pageIndex * (sheetGrid?.perPage ?? 1),
                    (pageIndex + 1) * (sheetGrid?.perPage ?? 1),
                  );

                  return (
                    <div
                      key={`print-page-${pageIndex}`}
                      style={{
                        display: "grid",
                        boxSizing: "border-box",
                        width: `${(paper.pageWidthMm ?? 210) - 2 * SHEET_MARGIN_MM}mm`,
                        // A hair shorter than the printable area and clipped, so
                        // rounding or a slightly smaller margin in the print
                        // dialog can't spill a sliver onto an extra blank page.
                        height: `${(paper.pageHeightMm ?? 297) - 2 * SHEET_MARGIN_MM - PRINT_SAFETY_MM}mm`,
                        overflow: "hidden",
                        breakInside: "avoid",
                        gridTemplateColumns: `repeat(${sheetGrid?.columns ?? 1}, 1fr)`,
                        gridTemplateRows: `repeat(${sheetGrid?.rows ?? 1}, 1fr)`,
                        justifyItems: "center",
                        alignItems: "center",
                        gap: `${LABEL_GAP_MM}mm`,
                        breakAfter:
                          pageIndex < Math.ceil(items.length / (sheetGrid?.perPage ?? 1)) - 1
                            ? "page"
                            : "auto",
                      }}
                    >
                      {pageItems.map((item, index) => {
                        const size = resolveItemPrintSize(item);
                        const design = resolveItemDesignSize(item);

                        return (
                          <LabelPreviewCard
                            key={`${item.id}-print-${pageIndex}-${index}`}
                            item={item}
                            layout={resolveItemLayout(item)}
                            widthMm={size.widthMm}
                            heightMm={size.heightMm}
                            designWidthMm={design.widthMm}
                            designHeightMm={design.heightMm}
                            rotationDegrees={rotationDegrees}
                            zoomPercent={zoomPercent}
                            crop={cropToFill}
                          />
                        );
                      })}
                    </div>
                  );
                },
              )
            : Array.from(
                { length: Math.max(1, Math.ceil(items.length / rollLabelsPerPage)) },
                (_unused, pageIndex) => (
                  <div
                    key={`roll-page-${pageIndex}`}
                    style={{
                      width: `${rollOutputWidthMm}mm`,
                      height: `${rollPageHeightMm - ROLL_PRINT_SAFETY_MM}mm`,
                      overflow: "hidden",
                      breakInside: "avoid",
                      boxSizing: "border-box",
                      display: "grid",
                      gridTemplateColumns: `repeat(${rollColumns}, ${paper.labelWidthMm}mm)`,
                      gridAutoRows: `${paper.labelHeightMm}mm`,
                      columnGap: `${rollMiddleGapMm}mm`,
                      rowGap: `${rollRowGapMm}mm`,
                      paddingLeft: `${rollLeftMarginMm}mm`,
                      paddingRight: `${rollRightMarginMm}mm`,
                      alignItems: "start",
                      alignContent: "start",
                      breakAfter:
                        pageIndex < Math.ceil(items.length / rollLabelsPerPage) - 1
                          ? "page"
                          : "auto",
                    }}
                  >
                    {items
                      .slice(pageIndex * rollLabelsPerPage, (pageIndex + 1) * rollLabelsPerPage)
                      .map((item, index) => {
                        const design = resolveItemDesignSize(item);

                        return (
                          <LabelPreviewCard
                            key={`${item.id}-print-roll-${pageIndex}-${index}`}
                            item={item}
                            layout={resolveItemLayout(item)}
                            widthMm={paper.labelWidthMm}
                            heightMm={paper.labelHeightMm}
                            designWidthMm={design.widthMm}
                            designHeightMm={design.heightMm}
                            rotationDegrees={rotationDegrees}
                            zoomPercent={zoomPercent}
                            crop={cropToFill}
                          />
                        );
                      })}
                  </div>
                ),
              )}
        </div>,
        document.body,
      )}
    </>
  );
};
